// API client for D1 database backend
// Falls back to localStorage when API is unavailable (local dev)

const API_BASE = '/api'

export interface CategoryStat {
  categoryId: string
  uniqueSeen: number
  attempts: number
  correct: number
  wrong: number
}

export interface PlayerData {
  password: string
  name: string
  googleId?: string | null
  totalScore: number
  sessionsPlayed: number
  bestStreak: number
  totalCorrect: number
  totalQuestions: number
  perfectGames: number
  wordsLearned: { wordId: number; correctCount: number; wrongCount: number }[]
  categoriesPlayed: string[]
  achievements: { id: string; unlockedAt: string }[]
  categoryStats?: CategoryStat[]
}

export interface LeaderboardEntry {
  name: string
  totalScore: number
  sessionsPlayed: number
  bestStreak: number
  totalCorrect: number
  totalQuestions: number
  perfectGames: number
  lastPlayedAt: string
  createdAt?: string
  wordsLearned: number
  achievementsCount: number
  totalDuration: number
  password: string
}

export interface SaveRequest {
  password: string
  name: string
  sessionScore: number
  sessionCorrect: number
  sessionTotal: number
  sessionDuration: number
  gameMode: string
  categories: string[]
  wordsPlayed: { wordId: number; correct: boolean; categoryId?: string }[]
  categoryStats?: CategoryStat[]
  bestStreak: number
  isPerfect: boolean
  achievements: { id: string; unlockedAt: string }[]
}

let useLocalFallback = false

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  if (useLocalFallback) {
    throw new Error('API unavailable, using local fallback')
  }
  try {
    const res = await fetch(`${API_BASE}${url}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...options?.headers },
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error((err as any).error || res.statusText)
    }
    return await res.json() as T
  } catch (e: any) {
    // If fetch itself fails (network/CORS), switch to local fallback
    if (e.message?.includes('fetch') || e.message?.includes('network') || e.name === 'TypeError') {
      useLocalFallback = true
    }
    throw e
  }
}

// ---------- Player Create ----------
export async function createPlayer(name: string, googleId?: string): Promise<PlayerData> {
  try {
    return await apiFetch<PlayerData>('/player/create', {
      method: 'POST',
      body: JSON.stringify({ name, googleId }),
    })
  } catch {
    // Local fallback
    return createPlayerLocal(name)
  }
}

// ---------- Player Login ----------
export async function loginPlayer(password: string): Promise<PlayerData> {
  try {
    return await apiFetch<PlayerData>('/player/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    })
  } catch {
    // Local fallback
    return loginPlayerLocal(password)
  }
}

// ---------- Player Login (Remote only, no fallback) ----------
export async function loginPlayerRemote(password: string): Promise<PlayerData | null> {
  try {
    return await apiFetch<PlayerData>('/player/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    })
  } catch {
    return null
  }
}

// ---------- Save Progress (Remote only, no fallback) ----------
export async function saveProgressRemote(req: SaveRequest): Promise<PlayerData | null> {
  try {
    return await apiFetch<PlayerData>('/player/save', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  } catch {
    return null
  }
}

// ---------- Create Player (Remote only, no fallback) ----------
export async function createPlayerRemote(name: string, googleId?: string): Promise<PlayerData | null> {
  try {
    return await apiFetch<PlayerData>('/player/create', {
      method: 'POST',
      body: JSON.stringify({ name, googleId }),
    })
  } catch {
    return null
  }
}

// ---------- Player Login by Google ----------
export async function loginPlayerGoogle(googleId: string): Promise<PlayerData> {
  return await apiFetch<PlayerData>('/player/login', {
    method: 'POST',
    body: JSON.stringify({ googleId }),
  })
}

// ---------- Save Progress ----------
export async function saveProgress(req: SaveRequest): Promise<PlayerData> {
  try {
    return await apiFetch<PlayerData>('/player/save', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  } catch {
    // Local fallback
    return saveProgressLocal(req)
  }
}

// ---------- Check Name Available ----------
export async function checkNameAvailable(name: string, currentPassword?: string): Promise<boolean> {
  try {
    const data = await apiFetch<{ available: boolean }>('/player/check-name', {
      method: 'POST',
      body: JSON.stringify({ name, currentPassword }),
    })
    return data.available
  } catch {
    // Local fallback
    return checkNameAvailableLocal(name, currentPassword)
  }
}

// ---------- Rename Player ----------
export async function renamePlayer(password: string, newName: string): Promise<{ success: boolean; name: string }> {
  try {
    return await apiFetch<{ success: boolean; name: string }>('/player/rename', {
      method: 'POST',
      body: JSON.stringify({ password, newName }),
    })
  } catch {
    // Local fallback
    return renamePlayerLocal(password, newName)
  }
}

export async function backfillCategoryStats(
  password: string,
  categoryStats: { categoryId: string; uniqueSeen: number; attempts: number; correct: number; wrong: number }[]
): Promise<{ success: boolean; updated: number } | null> {
  try {
    return await apiFetch<{ success: boolean; updated: number }>('/player/backfill-category-stats', {
      method: 'POST',
      body: JSON.stringify({ password, categoryStats }),
    })
  } catch {
    return null
  }
}

// ---------- Leaderboard ----------
export async function getLeaderboard(): Promise<LeaderboardEntry[]> {
  try {
    const data = await apiFetch<{ entries: LeaderboardEntry[] }>('/leaderboard')
    return data.entries
  } catch {
    // Local fallback
    return getLeaderboardLocal()
  }
}

// ---------- Master Mode: Save ----------
export interface MasterModeSaveRequest {
  password: string
  categories: string[]
  totalWords: number
  gamesCompleted: number
  isComplete: boolean
  totalScore: number
  totalTime: number
  overallAccuracy: number
  maxStreak: number
  gameStats: Record<string, { correct: number; total: number; time: number; score: number; maxStreak: number }>
}

export async function saveMasterSession(req: MasterModeSaveRequest): Promise<{ success: boolean }> {
  try {
    return await apiFetch<{ success: boolean }>('/master/save', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  } catch {
    // Local fallback - save to localStorage
    saveMasterSessionLocal(req)
    return { success: true }
  }
}

function saveMasterSessionLocal(req: MasterModeSaveRequest): void {
  const key = 'hsk2-master-mode-results'
  const existing: MasterLeaderboardEntry[] = JSON.parse(localStorage.getItem(key) || '[]')
  const playerName = localStorage.getItem('hsk2-player-name') || 'Player'
  existing.unshift({
    id: Date.now(),
    playerName,
    playerId: req.password,
    categories: req.categories,
    totalWords: req.totalWords,
    gamesCompleted: req.gamesCompleted,
    isComplete: req.isComplete,
    totalScore: req.totalScore,
    totalTime: req.totalTime,
    overallAccuracy: req.overallAccuracy,
    maxStreak: req.maxStreak,
    gameStats: req.gameStats,
    date: new Date().toISOString(),
  })
  localStorage.setItem(key, JSON.stringify(existing.slice(0, 50)))
}

// ---------- Master Mode: Leaderboard ----------
export interface MasterLeaderboardEntry {
  id: number
  playerName: string
  playerId: string
  categories: string[]
  totalWords: number
  gamesCompleted: number
  isComplete: boolean
  totalScore: number
  totalTime: number
  overallAccuracy: number
  maxStreak: number
  gameStats: Record<string, { correct: number; total: number; time: number; score: number; maxStreak: number }>
  date: string
}

export async function getMasterLeaderboard(): Promise<MasterLeaderboardEntry[]> {
  try {
    const data = await apiFetch<{ entries: MasterLeaderboardEntry[] }>('/master/leaderboard')
    return data.entries
  } catch {
    // Local fallback
    return getMasterLeaderboardLocal()
  }
}

function getMasterLeaderboardLocal(): MasterLeaderboardEntry[] {
  const key = 'hsk2-master-mode-results'
  try {
    return JSON.parse(localStorage.getItem(key) || '[]')
  } catch {
    return []
  }
}

// ============================================================
// LOCAL STORAGE FALLBACK (works the same as before for dev/offline)
// ============================================================

const PLAYERS_KEY = 'hsk2-players'

interface LocalPlayer {
  password: string
  name: string
  totalScore: number
  sessionsPlayed: number
  wordsLearned: number[]
  bestStreak: number
  totalCorrect: number
  totalQuestions: number
  perfectGames: number
  categoriesPlayed: string[]
  achievements: { id: string; unlockedAt: string }[]
  lastPlayedAt: string
}

function loadPlayersLocal(): Record<string, LocalPlayer> {
  const saved = localStorage.getItem(PLAYERS_KEY)
  return saved ? JSON.parse(saved) : {}
}

function savePlayersLocal(players: Record<string, LocalPlayer>) {
  localStorage.setItem(PLAYERS_KEY, JSON.stringify(players))
}

function generatePasswordLocal(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  let result = ''
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

function localToPlayerData(p: LocalPlayer): PlayerData {
  return {
    password: p.password,
    name: p.name,
    totalScore: p.totalScore,
    sessionsPlayed: p.sessionsPlayed,
    bestStreak: p.bestStreak,
    totalCorrect: p.totalCorrect,
    totalQuestions: p.totalQuestions,
    perfectGames: p.perfectGames,
    wordsLearned: p.wordsLearned.map(id => ({ wordId: id, correctCount: 1, wrongCount: 0 })),
    categoriesPlayed: p.categoriesPlayed,
    achievements: p.achievements,
    categoryStats: [],
  }
}

function createPlayerLocal(name: string): PlayerData {
  const players = loadPlayersLocal()
  const password = generatePasswordLocal()
  const now = new Date().toISOString()
  const player: LocalPlayer = {
    password,
    name: name.trim(),
    totalScore: 0,
    sessionsPlayed: 0,
    wordsLearned: [],
    bestStreak: 0,
    totalCorrect: 0,
    totalQuestions: 0,
    perfectGames: 0,
    categoriesPlayed: [],
    achievements: [],
    lastPlayedAt: now,
  }
  players[password] = player
  savePlayersLocal(players)
  return localToPlayerData(player)
}

function loginPlayerLocal(password: string): PlayerData {
  const players = loadPlayersLocal()
  const p = players[password]
  if (!p) throw new Error('Player not found')
  return localToPlayerData(p)
}

function saveProgressLocal(req: SaveRequest): PlayerData {
  const players = loadPlayersLocal()
  const p = players[req.password]
  if (!p) throw new Error('Player not found')

  p.name = req.name || p.name
  p.totalScore += req.sessionScore
  p.sessionsPlayed += 1
  p.bestStreak = Math.max(p.bestStreak, req.bestStreak)
  p.totalCorrect += req.sessionCorrect
  p.totalQuestions += req.sessionTotal
  if (req.isPerfect) p.perfectGames += 1
  p.lastPlayedAt = new Date().toISOString()

  // Add new words
  req.wordsPlayed.forEach(w => {
    if (w.correct && !p.wordsLearned.includes(w.wordId)) {
      p.wordsLearned.push(w.wordId)
    }
  })

  // Add new categories
  req.categories.forEach(cat => {
    if (!p.categoriesPlayed.includes(cat)) {
      p.categoriesPlayed.push(cat)
    }
  })

  // Add new achievements
  req.achievements.forEach(a => {
    if (!p.achievements.find(x => x.id === a.id)) {
      p.achievements.push(a)
    }
  })

  players[req.password] = p
  savePlayersLocal(players)
  return localToPlayerData(p)
}

function checkNameAvailableLocal(name: string, currentPassword?: string): boolean {
  const players = loadPlayersLocal()
  const lowerName = name.toLowerCase().trim()
  return !Object.values(players).some(
    p => p.name.toLowerCase() === lowerName && p.password !== currentPassword
  )
}

function renamePlayerLocal(password: string, newName: string): { success: boolean; name: string } {
  const players = loadPlayersLocal()
  const p = players[password]
  if (!p) throw new Error('Player not found')
  const trimmed = newName.trim()
  if (!checkNameAvailableLocal(trimmed, password)) {
    throw new Error('Name is already taken')
  }
  p.name = trimmed
  players[password] = p
  savePlayersLocal(players)
  return { success: true, name: trimmed }
}

function getLeaderboardLocal(): LeaderboardEntry[] {
  const players = loadPlayersLocal()
  return Object.values(players)
    .map(p => ({
      name: p.name,
      totalScore: p.totalScore,
      sessionsPlayed: p.sessionsPlayed,
      bestStreak: p.bestStreak,
      totalCorrect: p.totalCorrect,
      totalQuestions: p.totalQuestions,
      perfectGames: p.perfectGames,
      lastPlayedAt: p.lastPlayedAt,
      wordsLearned: p.wordsLearned.length,
      achievementsCount: p.achievements.length,
      totalDuration: 0,
      password: p.password,
    }))
    .sort((a, b) => b.totalScore - a.totalScore)
}
